// Editing Season's ideal week. Rows of label / hours / optional goal / optional
// when, under the 168-hour bar. Nothing is pre-filled or suggested: every label and
// number is typed. A change is committed on Enter or when a field loses focus,
// never per keystroke.

import { useEffect, useId, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { formatHours, parseDuration, type IdealWeek } from '@/lib/guide/idealWeek'
import { IdealWeekBar, IdealWeekLeft, type IdealWeekGoal } from './IdealWeekSection'
import './guide.css'

type Block = IdealWeek['blocks'][number]

const enterBlurs = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur() }

function BlockFields({ initial, goals, onCommit, onRemove, fresh }: {
  initial: Block | null
  goals: IdealWeekGoal[]
  /** Return true when the row was accepted (a new row then clears). */
  onCommit: (b: Omit<Block, 'id'>) => boolean
  onRemove?: () => void
  fresh?: boolean
}) {
  const [label, setLabel] = useState(initial?.label ?? '')
  const [hours, setHours] = useState(initial ? formatHours(initial.hours) : '')
  const [goalId, setGoalId] = useState(initial?.goalId ?? '')
  const [when, setWhen] = useState(initial?.when ?? '')
  const [bad, setBad] = useState(false)
  const [seen, setSeen] = useState(initial)
  if (initial !== seen) {
    setSeen(initial)
    setLabel(initial?.label ?? ''); setHours(initial ? formatHours(initial.hours) : '')
    setGoalId(initial?.goalId ?? ''); setWhen(initial?.when ?? ''); setBad(false)
  }
  const name = initial?.label || 'new block'

  const commit = (over: Partial<{ label: string; goalId: string; when: string }> = {}) => {
    const l = (over.label ?? label).trim()
    const parsed = parseDuration(hours)
    if (hours.trim() && parsed === null) { setBad(true); return }
    setBad(false)
    if (!l || parsed === null) {
      if (!fresh && initial) { setLabel(initial.label); setHours(formatHours(initial.hours)) }
      return
    }
    // The amount as shown is rounded to the minute: the same minutes are the
    // same amount, so leaving the field untouched rewrites nothing.
    const h = initial && Math.round(parsed * 60) === Math.round(initial.hours * 60) ? initial.hours : parsed
    const g = over.goalId ?? goalId, w = (over.when ?? when).trim()
    const accepted = onCommit({ label: l, hours: h, ...(g ? { goalId: g } : {}), ...(w ? { when: w } : {}) })
    if (accepted && fresh) { setLabel(''); setHours(''); setGoalId(''); setWhen('') }
  }
  // A block already there saves as each field is left. The new row waits
  // until focus leaves the row: a goal or a "when" picked after the hours
  // lands on the new block, and leaving one half-typed field wipes nothing.
  // Where the next focus is not named (Safari does not focus a select or a
  // button it is clicked on), a press inside the row counts as staying in
  // it, and the row commits on the first press or focus outside it.
  const row = useRef<HTMLLIElement>(null)
  const pressedInside = useRef(false)
  const latestCommit = useRef(commit)
  useEffect(() => { latestCommit.current = commit })
  const stopWatching = useRef<(() => void) | null>(null)
  useEffect(() => () => stopWatching.current?.(), [])
  const commitOnLeavingRow = () => {
    if (stopWatching.current) return
    const outside = (e: Event) => {
      if (row.current?.contains(e.target as Node | null)) return
      stopWatching.current?.()
      latestCommit.current()
    }
    document.addEventListener('pointerdown', outside, true)
    document.addEventListener('focusin', outside, true)
    stopWatching.current = () => {
      document.removeEventListener('pointerdown', outside, true)
      document.removeEventListener('focusin', outside, true)
      stopWatching.current = null
    }
  }
  const leave = (e: React.FocusEvent<HTMLElement>) => {
    if (!fresh) { commit(); return }
    if (row.current?.contains(e.relatedTarget as Node | null)) return
    if (pressedInside.current) { commitOnLeavingRow(); return }
    stopWatching.current?.()
    commit()
  }
  const pressed = () => {
    if (!fresh) return
    pressedInside.current = true
    setTimeout(() => { pressedInside.current = false }, 0)
  }
  return (
    <li ref={row} className="guide-iw-edit-row" data-fresh={fresh || undefined} onPointerDown={pressed}>
      <input type="text" className="guide-iw-in guide-iw-in-label" aria-label={`Label for ${name}`} placeholder="Label"
        value={label} onChange={e => setLabel(e.target.value)} onBlur={leave} onKeyDown={enterBlurs} />
      <input type="text" inputMode="decimal" className="guide-iw-in guide-iw-in-hours" aria-label={`Hours for ${name}`} placeholder="Hours per week"
        aria-invalid={bad || undefined} value={hours} onChange={e => { setHours(e.target.value); setBad(false) }} onBlur={leave} onKeyDown={enterBlurs} />
      <select className="guide-iw-in guide-iw-in-goal" aria-label={`Goal for ${name}`} value={goalId}
        onChange={e => { setGoalId(e.target.value); if (!fresh) commit({ goalId: e.target.value }) }}
        onBlur={fresh ? leave : undefined}>
        <option value="">Not a goal</option>
        {goals.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
        {goalId && !goals.some(g => g.id === goalId) && <option value={goalId}>(goal no longer here)</option>}
      </select>
      <input type="text" className="guide-iw-in guide-iw-in-when" aria-label={`When for ${name}`} placeholder="When (optional)"
        value={when} onChange={e => setWhen(e.target.value)} onBlur={leave} onKeyDown={enterBlurs} />
      {onRemove
        ? <button type="button" className="guide-areas-icon" aria-label={`Remove ${name}`} onClick={onRemove}><Trash2 size={15} aria-hidden="true" /></button>
        : <span className="guide-iw-spacer" aria-hidden="true" />}
      {bad && <span className="guide-iw-bad" role="alert">Try 3h 45m, 3.75, or 3 × 1h 15m</span>}
    </li>
  )
}

export interface IdealWeekEditorProps {
  value: IdealWeek
  goals: IdealWeekGoal[]
  onChange: (w: IdealWeek) => void
}

export function IdealWeekEditor({ value, goals, onChange }: IdealWeekEditorProps) {
  const uid = useId()
  const update = (id: string, b: Omit<Block, 'id'>) => {
    const old = value.blocks.find(x => x.id === id)
    if (old && old.label === b.label && old.hours === b.hours && old.goalId === b.goalId && old.when === b.when) return true
    onChange({ blocks: value.blocks.map(x => (x.id === id ? { id, ...b } : x)) })
    return true
  }
  const add = (b: Omit<Block, 'id'>) => {
    onChange({ blocks: [...value.blocks, { id: `${uid}-${value.blocks.length}-${Date.now().toString(36)}`, ...b }] })
    return true
  }
  return (
    <div className="guide-iw guide-iw-editor">
      <div className="guide-iw-top"><IdealWeekLeft value={value} /></div>
      <IdealWeekBar value={value} />
      <ul className="guide-iw-edit-list" aria-label="Ideal week blocks">
        {value.blocks.map(b => (
          <BlockFields key={b.id} initial={b} goals={goals}
            onCommit={nb => update(b.id, nb)} onRemove={() => onChange({ blocks: value.blocks.filter(x => x.id !== b.id) })} />
        ))}
        <BlockFields key="new" initial={null} goals={goals} fresh onCommit={add} />
      </ul>
    </div>
  )
}
